import { useQuery } from "@tanstack/react-query";

import {
  IMeasurementTypeCatalogItem,
  IMeasurementTypeTemplate,
  IWardMeasurementType,
} from "../../../@types/admin/AdminMeasurementType";
import { measurementTypesService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/** Query key the mutations invalidate. */
export function wardMeasurementTypesKey(wardId: number | undefined) {
  return ["admin-ward-measurement-types", wardId];
}

export function availableMeasurementTypesKey(wardId: number | undefined) {
  return ["admin-ward-measurement-types-available", wardId];
}

/** The selected ward's configured measurement types. */
export function useWardMeasurementTypes(wardId: number | undefined) {
  const { data, isLoading, isError, isFetched } = useQuery<
    IWardMeasurementType[]
  >({
    queryKey: wardMeasurementTypesKey(wardId),
    queryFn: async () =>
      ensureOk(await measurementTypesService.getWardMeasurementTypes(wardId!)),
    enabled: wardId !== undefined,
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return {
    wardMeasurementTypes: data ?? [],
    isLoading,
    isError,
    // The empty-ward warning must not show before the list has actually
    // loaded: "this ward has no config" and "we haven't read it yet" look the
    // same from an empty array.
    isLoaded: isFetched && !isError,
  };
}

/**
 * Catalogue entries not yet on the ward. Retool fetched this only when the
 * add modal opened; here it is enabled by the modal being mounted.
 */
export function useAvailableMeasurementTypes(wardId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IMeasurementTypeCatalogItem[]>({
    queryKey: availableMeasurementTypesKey(wardId),
    queryFn: async () =>
      ensureOk(
        await measurementTypesService.getAvailableMeasurementTypes(wardId!)
      ),
    enabled: wardId !== undefined,
    refetchOnMount: "always",
  });

  return { availableMeasurementTypes: data ?? [], isLoading, isError };
}

/** Active templates with their items. Catalogue-sized and stable. */
export function useMeasurementTypeTemplates() {
  const { data, isLoading, isError } = useQuery<IMeasurementTypeTemplate[]>({
    queryKey: ["admin-measurement-type-templates"],
    queryFn: async () =>
      ensureOk(await measurementTypesService.getMeasurementTypeTemplates()),
  });

  return { templates: data ?? [], isLoading, isError };
}
